import { CreditCardOutlined, LineChartOutlined, UserOutlined } from '@ant-design/icons';
import { ConfigProvider, Flex, Grid, Menu, Segmented, Typography, theme } from 'antd';
import { Outlet, useLocation, useNavigate } from 'react-router';

/**
 * Configurações: onde mora o que é da pessoa, e não do estudo.
 *
 * Até 02/09/2026 "Minha assinatura" era uma tela solta, alcançável só pelo menu
 * do perfil, e "Planos" era outra. Duas telas irmãs em dois lugares diferentes,
 * e nenhum lugar para pôr a terceira - o desempenho, a conta, o que vier
 * depois. Configurações é esse lugar: uma coluna de assuntos e uma área de
 * conteúdo, o formato que todo SaaS usa porque ele aguenta crescer.
 *
 * A coluna é `Menu`, e não `Tabs`: cada assunto tem endereço próprio
 * (`/configuracoes/conta`), então recarregar a página ou compartilhar o link
 * cai no lugar certo - e o retorno do pagamento, que chega do provedor por uma
 * URL, precisa disso.
 *
 * No celular a coluna vira uma faixa de opções no topo. Uma coluna de 240px
 * numa tela de 390 deixaria 150 para o conteúdo.
 */
const ASSUNTOS = [
  { chave: 'conta', rotulo: 'Conta', icone: <UserOutlined /> },
  { chave: 'desempenho', rotulo: 'Desempenho', icone: <LineChartOutlined /> },
  /*
   * **Um assunto só para o plano, e não dois.** Até 05/09/2026 havia
   * "Assinatura" (o que você comprou) e "Planos" (a vitrine), lado a lado no
   * menu - duas entradas para a mesma pergunta, e a segunda existindo apenas
   * para hospedar um botão de compra. A Voa tem "Plano", no singular, e é o
   * desenho certo: a tela diz o estado da conta, e quem vende é a oferta, que
   * abre por cima de onde a pessoa estiver.
   */
  { chave: 'plano', rotulo: 'Plano', icone: <CreditCardOutlined /> },
];

/**
 * A coluna de assuntos, medida na Voa: 240px, item de 36px, fio de separação à
 * direita. Ela **encosta na barra do app**, sem vão entre as duas navegações -
 * é o que faz Configurações parecer uma segunda camada da mesma barra, e não
 * uma ilha no meio da tela.
 */
const LARGURA_DA_COLUNA = 240;

/** Onde o conteúdo para de crescer. Ajuste não se lê em linha de 1200px. */
const LARGURA_DO_CONTEUDO = 760;

export function ConfiguracoesLayout() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();
  const telas = Grid.useBreakpoint();

  /*
   * O assunto sai do endereço, e não de estado local. O plano tem um segundo
   * trecho (`/plano/sucesso`, por onde o provedor devolve quem pagou), então é
   * o começo do caminho que decide, não a igualdade.
   */
  const atual =
    ASSUNTOS.find((assunto) =>
      location.pathname.startsWith(`/configuracoes/${assunto.chave}`),
    )?.chave ?? 'conta';

  const ir = (chave: string) => void navigate(`/configuracoes/${chave}`);

  const coluna = (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            /*
             * Cinza, e não a cor da marca. Aqui a seleção diz "você está
             * aqui", não "isto é importante": a barra lateral do app já usa o
             * verde para o módulo em estudo, e repeti-lo dentro de
             * Configurações faria dois destaques disputarem a mesma tela.
             */
            itemSelectedBg: token.colorFillSecondary,
            itemSelectedColor: token.colorText,
            itemHeight: 36,
            itemBorderRadius: token.borderRadius,
            itemMarginInline: 0,
          },
        },
      }}
    >
      <Menu
        mode="inline"
        selectedKeys={[atual]}
        style={{ borderInlineEnd: 'none', background: 'transparent' }}
        onClick={({ key }) => ir(key)}
        items={ASSUNTOS.map((assunto) => ({
          key: assunto.chave,
          icon: assunto.icone,
          label: assunto.rotulo,
        }))}
      />
    </ConfigProvider>
  );

  if (!telas.md) {
    return (
      // O recuo é daqui: o `Layout.Content` entrega Configurações sem padding,
      // justamente para a coluna do desktop poder encostar na barra do app.
      <Flex vertical gap={20} style={{ padding: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Configurações
        </Typography.Title>
        {/*
          Sem ícone, cada opção com a largura do próprio rótulo, e a faixa
          rolando de lado se não couber.

          As duas alternativas foram piores. Com ícone, os assuntos passam de
          390px e sobra rolagem. Com `block`, eles cabem à força e o Antd corta
          o rótulo - o usuário perde a palavra para ganhar simetria.

          A barra de rolagem some (`scrollbarWidth: none`) porque, quando não
          cabe, o último item aparece pela metade - e meia palavra visível diz
          "tem mais aqui" melhor que uma barra cinza de 4px.
        */}
        <div style={{ overflowX: 'auto', scrollbarWidth: 'none' }}>
          <Segmented
            value={atual}
            onChange={(valor) => ir(String(valor))}
            options={ASSUNTOS.map((assunto) => ({
              value: assunto.chave,
              label: assunto.rotulo,
            }))}
          />
        </div>
        <Outlet />
      </Flex>
    );
  }

  return (
    <Flex align="stretch" style={{ minHeight: '100%' }}>
      <div
        style={{
          width: LARGURA_DA_COLUNA,
          flexShrink: 0,
          paddingBlock: 16,
          paddingInline: 8,
          /*
           * O fio que separa as duas colunas, e não um vão. Vão faria a coluna
           * flutuar; o fio a prende à barra do app, que já termina em outro
           * fio igual - as duas leem como uma navegação de dois níveis.
           */
          borderInlineEnd: `1px solid ${token.colorSplit}`,
        }}
      >
        {/*
          Gruda no topo ao rolar. O conteúdo pode passar da altura da coluna, e
          perder a navegação no meio da rolagem é o defeito clássico deste
          desenho.
        */}
        <div style={{ position: 'sticky', top: 16 }}>
          <Typography.Title level={5} style={{ margin: '0 0 12px', paddingInline: 12 }}>
            Configurações
          </Typography.Title>
          {coluna}
        </div>
      </div>

      {/*
        O conteúdo não cresce sem fim: numa tela de 1600px, uma linha de ajuste
        com o rótulo à esquerda e o controle à direita ficaria com meio metro de
        vazio no meio, e o olho perderia a ligação entre os dois.
      */}
      <div style={{ flex: 1, minWidth: 0, padding: 24 }}>
        <div style={{ maxWidth: LARGURA_DO_CONTEUDO }}>
          <Outlet />
        </div>
      </div>
    </Flex>
  );
}
